import React, { ForwardedRef } from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { styled } from '../../stitches.config';
import Box from './Box';

const SCROLLBAR_SIZE = 10;

const StyledScrollArea = styled(ScrollAreaPrimitive.Root, {
  display: 'flex',
  borderRadius: 4,
  overflow: 'hidden',
  width: '100%',
});

const StyledViewport = styled(ScrollAreaPrimitive.Viewport, {
  width: '100%',
  minHeight: '100%',
});

const StyledScrollbar = styled(ScrollAreaPrimitive.Scrollbar, {
  display: 'flex',
  // ensures no selection
  userSelect: 'none',
  // disable browser handling of all panning and zooming gestures on touch devices
  touchAction: 'none',
  padding: 2,
  background: '$canvasMuted',
  transition: 'background 160ms ease-out',
  '&[data-orientation="vertical"]': {
    width: SCROLLBAR_SIZE,
  },
  '&[data-orientation="horizontal"]': {
    flexDirection: 'column',
    height: SCROLLBAR_SIZE,
  },
});

const StyledThumb = styled(ScrollAreaPrimitive.Thumb, {
  flex: 1,
  background: '$borderMuted',
  borderRadius: SCROLLBAR_SIZE,
  // increase target size for touch devices https://www.w3.org/WAI/WCAG21/Understanding/target-size.html
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    minWidth: 44,
    minHeight: 44,
  },
});

// Exports
export const ScrollArea = StyledScrollArea;
export const ScrollAreaViewport = StyledViewport;
export const ScrollAreaScrollbar = StyledScrollbar;
export const ScrollAreaThumb = StyledThumb;

const Scroll = React.forwardRef(
  (
    {
      children,
      maxWidth,
      height,
      width,
    }: {
      children: React.ReactNode;
      maxWidth?: string;
      height: string;
      width?: string;
    },
    ref: ForwardedRef<HTMLDivElement>
  ) => (
    <ScrollArea
      css={{
        height,
        width,
        maxWidth,
        padding: 0,
      }}
      type="auto"
    >
      <ScrollAreaViewport ref={ref}>
        <Box>{children}</Box>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="vertical">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
      <ScrollAreaScrollbar orientation="horizontal">
        <ScrollAreaThumb />
      </ScrollAreaScrollbar>
    </ScrollArea>
  )
);

export default Scroll;
